import React, { useEffect, useState, useMemo, memo } from 'react';
import type {
  QuestionDimension,
  QuestionGrid,
  QuestionRange,
} from '@mentimeter/http-clients';
import { usePrevious } from '@mentimeter/react-hooks';
import { Rating, type RatingProps } from '@mentimeter/visualizations';
import type { RatingT } from '@mentimeter/question-modules-types';
import { useRagnar } from '@mentimeter/ragnar-react';
import { VisualizationUpdater } from '../../../shared/components/visualizations-legacy/VisualizationUpdater';
import { SvgElement } from '../../../shared/components/visualizations-legacy/SvgElement';
import { getColors } from '../../../shared/utils/getColors';
import { useUniqueSelector } from '../../../shared/hooks/useUniqueSelector';
import { mapScatterNodes } from '../utils/mapScatterNodes';
import light2x2File from './2x2light.svg';
import dark2x2File from './2x2dark.svg';

export const X_LABEL_DEFAULT = 'Effort';
export const Y_LABEL_DEFAULT = 'Impact';

const LAYOUT = {
  width: 960,
  height: 540,
};
const MARGIN = {
  top: LAYOUT.height * 0.12,
  right: LAYOUT.width * 0.05,
  bottom: LAYOUT.height * 0.07,
  left: LAYOUT.width * 0.05,
};
const gridSize = LAYOUT.height - MARGIN.top - MARGIN.bottom;
const AXIS_PADDING = 35;

const gridPositionAndSize: RatingProps['gridPositionAndSize'] = {
  top: 0,
  right: gridSize,
  bottom: gridSize,
  left: 0,
  width: gridSize,
  height: gridSize,
};

type RatingGridProps = Pick<
  RatingProps,
  | 'backgroundColor'
  | 'colors'
  | 'enableAnimations'
  | 'grid'
  | 'is4Values'
  | 'labels'
  | 'lineColor'
  | 'range'
  | 'respondents'
  | 'results'
  | 'scatterNodes'
  | 'selector'
  | 'textColor'
  | 'textColors'
>;

const LegacyRatingGrid = memo<RatingGridProps>(
  ({
    selector,
    range,
    colors,
    textColors,
    textColor,
    enableAnimations,
    grid,
    results,
    labels,
    scatterNodes,
    respondents,
    lineColor,
    backgroundColor,
    is4Values,
  }) => {
    const [hoverId, setHoverId] = useState<RatingProps['hoverId']>(null);
    const [nodes, setNodes] = useState<RatingProps['nodes']>([]);

    // Prev props
    const previousNodes = usePrevious(nodes, []);

    // Set nodes
    useEffect(() => {
      const nodes = results.map((r, i) => ({
        ...r,
        // The force simulation assigns x and y values of 0 initially, then interpolating to
        // the values specified in the dataset. We want to use previous values if they are available,
        // so the nodes go from previousPosition --> newPosition instead of originPosition --> newPosition
        x: previousNodes[i]?.x || 0,
        y: previousNodes[i]?.y || 0,
      }));
      const scores = nodes.flatMap((n) => n.score);
      const previousScores = previousNodes.flatMap((n) => n.score);
      const scoresChanged =
        scores.findIndex((s, i) => {
          const changedScore = s !== null && s !== previousScores[i];
          const clearedScore = s === null && previousScores[i] !== null;
          return changedScore || clearedScore;
        }) !== -1;
      const nodesChanged = previousNodes.length === 0 && nodes.length > 0;
      if (scoresChanged || nodesChanged) {
        setNodes(nodes);
      }
    }, [results, previousNodes]);

    const rating = new Rating({
      selector: `#${selector}`,
      range,
      gridPositionAndSize,
      labels,
      textColor,
      lineColor,
      is4Values,
      backgroundColor,
      grid,
      results,
      respondents,
      enableAnimations,
      nodes,
      scatterNodes,
      colors,
      textColors,
      hoverId,
      setHoverId,
    });

    return (
      <SvgElement data-testid="rating-svg" aria-hidden="true">
        <VisualizationUpdater updateFn={rating.update} />
        <g id={selector} />
      </SvgElement>
    );
  },
);

interface RatingGridDataHandlerProps {
  dimensions: [QuestionDimension, QuestionDimension];
  range: QuestionRange;
  results: Pick<RatingT, 'results' | 'votes' | 'respondents'>;
  enableAnimations: boolean;
  grid: QuestionGrid;
}

export const LegacyRatingGridDataHandler = ({
  dimensions,
  range,
  results,
  grid,
  enableAnimations,
}: RatingGridDataHandlerProps) => {
  const ratingGrid = grid.url
    ? grid
    : ({
        id: '2x2',
        type: 'default',
        name: '2 by 2',
        dark: dark2x2File.src,
        bright: light2x2File.src,
        editable: false,
      } as const);

  const {
    theme: {
      visualizationColors: { fillColors, textColor, backgroundColor },
    },
  } = useRagnar();
  const selector = useUniqueSelector();

  const { barColors, textColors } = useMemo(
    () => getColors(fillColors),
    [fillColors],
  );
  const labels = useMemo<RatingProps['labels']>(() => {
    const X = 0;
    const Y = 1;

    return new Array<RatingProps['labels'][number]>(
      {
        xPos: gridSize / 2,
        yPos: gridSize + AXIS_PADDING,
        xOffset: 0,
        yOffset: 0,
        rotation: 0,
        label: dimensions[X]?.label ?? X_LABEL_DEFAULT,
        min: '',
        max: '',
      },
      {
        xPos: -AXIS_PADDING,
        yPos: gridSize / 2,
        xOffset: 0,
        yOffset: 0,
        rotation: 270,
        label: dimensions[Y]?.label ?? Y_LABEL_DEFAULT,
        min: '',
        max: '',
      },
    );
  }, [dimensions]);

  const scatterNodes = useMemo(
    () =>
      mapScatterNodes({
        results: results.results,
        votes: results.votes,
        barColors,
      }),
    [results, barColors],
  );

  return (
    <LegacyRatingGrid
      selector={selector}
      range={range}
      labels={labels}
      scatterNodes={scatterNodes}
      colors={barColors}
      textColors={textColors}
      textColor={textColor}
      backgroundColor={backgroundColor}
      enableAnimations={enableAnimations}
      grid={ratingGrid}
      lineColor={textColor}
      results={results.results}
      respondents={results.respondents}
      is4Values={false}
    />
  );
};
